/**
 * RoleRepository.ts - RBAC Role Management Repository
 *
 * FERP Repository Pattern - Frontend data access layer for RBAC operations
 *
 * GitHub Issue: #336
 * Week: 2 (Frontend Integration)
 *
 * FERP Principles:
 * - Repository Pattern: All RBAC data access through this layer
 * - Data-Driven: All values from GraphQL backend
 * - Immutable Audit: All operations tracked
 * - Multi-Tenant: Complete tenant isolation
 * - Type-Safe: Full TypeScript type definitions
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Type Definitions (matching backend GraphQL schema)
// ============================================================================

/**
 * Role Definition
 *
 * Complete role with scopes and capabilities
 */
export interface RoleDefinition {
  roleId: string;
  tenantId: string;
  roleName: string;
  displayName: string;
  description?: string;
  scopes: string[];
  capabilities: string[];
  inheritsFrom: string[];
  enabled: boolean;
  version?: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/**
 * User Role Assignment
 *
 * Immutable record of role assignment to user
 */
export interface UserRoleAssignment {
  assignmentId: string;
  userId: string;
  tenantId: string;
  roleId: string;
  role?: RoleDefinition;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  revoked: boolean;
  revokedBy?: string;
  revokedAt?: string;
  revocationReason?: string;
}

/**
 * User Role Response
 *
 * Current user's effective role with computed permissions
 */
export interface UserRoleResponse {
  role: string;
  tenant: string;
  scopes: string[];
  capabilities: string[];
}

/**
 * User Permissions Response
 *
 * Computed permissions for a role (including inherited)
 */
export interface UserPermissionsResponse {
  permissions: string[];
  tenant: string;
  role: string;
  computedAt: string;
}

/**
 * Effective Role
 *
 * Role with computed inheritance chain
 */
export interface EffectiveRole {
  roleId: string;
  roleName: string;
  displayName: string;
  effectiveScopes: string[];
  effectiveCapabilities: string[];
  inheritanceChain: string[];
}

/**
 * Role Statistics
 *
 * Analytics for tenant roles
 */
export interface RoleStatistics {
  totalRoles: number;
  enabledRoles: number;
  disabledRoles: number;
  totalAssignments: number;
  activeAssignments: number;
}

// ============================================================================
// Input Types (for mutations)
// ============================================================================

export interface CreateRoleInput {
  tenantId: string;
  roleName: string;
  displayName: string;
  description?: string;
  scopes: string[];
  capabilities: string[];
  inheritsFrom?: string[];
  enabled: boolean;
}

export interface UpdateRoleInput {
  roleId: string;
  tenantId: string;
  displayName?: string;
  description?: string;
  scopes?: string[];
  capabilities?: string[];
  inheritsFrom?: string[];
  enabled?: boolean;
}

export interface AssignRoleInput {
  userId: string;
  tenantId: string;
  roleId: string;
  expiresAt?: string;
}

export interface RevokeRoleInput {
  userId: string;
  tenantId: string;
  roleId: string;
  reason: string;
}

// ============================================================================
// RoleRepository Class
// ============================================================================

/**
 * RoleRepository - Frontend RBAC data access layer
 *
 * Extends BaseRepository for:
 * - Safety validation
 * - Audit trail integration
 * - Error handling
 * - Retry logic
 */
export class RoleRepository extends BaseRepository<RoleDefinition> {
  private apolloClient: ApolloClient;

  constructor(
    client: ApolloClient,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    auditTrail?: any
  ) {
    super();
    this.apolloClient = client;
    if (auditTrail) {
      this.auditTrail = auditTrail;
    }
  }

  // ==========================================================================
  // Query Operations
  // ==========================================================================

  /**
   * Get current user's role for a tenant
   *
   * @param tenantId - Tenant ID
   * @returns User's current role with effective permissions
   */
  async getCurrentUserRole(tenantId: string): Promise<UserRoleResponse> {
    await this.validateRepositorySafety({
      operation: 'GET_CURRENT_USER_ROLE',
      domain: 'RBAC',
      tenantId,
      entityType: 'UserRole',
      criticalityLevel: 'MEDIUM',
      metadata: { tenantId }
    });

    const query = gql`
      query GetCurrentUserRole($tenantId: String!) {
        currentUserRole(tenantId: $tenantId) {
          role
          tenant
          scopes
          capabilities
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      const roleData = result.data?.currentUserRole;

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'GET_CURRENT_USER_ROLE',
          tenantId,
          entityType: 'UserRole',
          entityId: 'current',
          metadata: { role: roleData.role, scopeCount: roleData.scopes.length },
          timestamp: Date.now()
        });
      }

      return roleData;
    } catch (error) {
      console.error('[RoleRepository] Failed to get current user role:', error);
      throw error;
    }
  }

  /**
   * Get user permissions for a role
   *
   * @param tenantId - Tenant ID
   * @param userRole - Role name
   * @returns Computed permissions (including inherited)
   */
  async getUserPermissions(tenantId: string, userRole: string): Promise<UserPermissionsResponse> {
    await this.validateRepositorySafety({
      operation: 'GET_USER_PERMISSIONS',
      domain: 'RBAC',
      tenantId,
      entityType: 'UserPermissions',
      criticalityLevel: 'MEDIUM',
      metadata: { tenantId, userRole }
    });

    const query = gql`
      query GetUserPermissions($tenantId: String!, $userRole: String!) {
        userPermissions(tenantId: $tenantId, userRole: $userRole) {
          permissions
          tenant
          role
          computedAt
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId, userRole },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      return result.data?.userPermissions ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to get user permissions:', error);
      throw error;
    }
  }

  /**
   * List all roles for a tenant (admin only)
   *
   * @param tenantId - Tenant ID
   * @returns Array of role definitions
   */
  async listRoles(tenantId: string): Promise<RoleDefinition[]> {
    await this.validateRepositorySafety({
      operation: 'LIST_ROLES',
      domain: 'RBAC',
      tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'LOW',
      metadata: { tenantId }
    });

    const query = gql`
      query ListRoles($tenantId: String!) {
        listRoles(tenantId: $tenantId) {
          roleId
          tenantId
          roleName
          displayName
          description
          scopes
          capabilities
          inheritsFrom
          enabled
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId },
        context: {
          headers: { 'x-tenant': tenantId }
        },
        fetchPolicy: 'network-only' // Always fetch fresh data for admin operations
      });

      // Graceful null handling - return empty array if no roles
      return result.data?.listRoles || [];
    } catch (error) {
      console.error('[RoleRepository] Failed to list roles:', error);
      // Return empty array instead of throwing - graceful degradation
      return [];
    }
  }

  /**
   * Get role by ID (admin only)
   *
   * @param tenantId - Tenant ID
   * @param roleId - Role ID
   * @returns Role definition or null
   */
  async getRole(tenantId: string, roleId: string): Promise<RoleDefinition | null> {
    await this.validateRepositorySafety({
      operation: 'GET_ROLE',
      domain: 'RBAC',
      tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'LOW',
      metadata: { tenantId, roleId }
    });

    const query = gql`
      query GetRole($tenantId: String!, $roleId: String!) {
        getRole(tenantId: $tenantId, roleId: $roleId) {
          roleId
          tenantId
          roleName
          displayName
          description
          scopes
          capabilities
          inheritsFrom
          enabled
          version
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId, roleId },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      return result.data?.getRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to get role:', error);
      throw error;
    }
  }

  /**
   * Get effective role (with computed inheritance)
   *
   * @param tenantId - Tenant ID
   * @param roleId - Role ID
   * @returns Effective role with flattened permissions
   */
  async getEffectiveRole(tenantId: string, roleId: string): Promise<EffectiveRole | null> {
    const query = gql`
      query GetEffectiveRole($tenantId: String!, $roleId: String!) {
        getEffectiveRole(tenantId: $tenantId, roleId: $roleId) {
          roleId
          roleName
          displayName
          effectiveScopes
          effectiveCapabilities
          inheritanceChain
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId, roleId },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      return result.data?.getEffectiveRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to get effective role:', error);
      throw error;
    }
  }

  /**
   * Get user's role assignments (admin only)
   *
   * @param tenantId - Tenant ID
   * @param userId - User ID
   * @returns Array of role assignments
   */
  async getUserRoles(tenantId: string, userId: string): Promise<UserRoleAssignment[]> {
    const query = gql`
      query GetUserRoles($tenantId: String!, $userId: String!) {
        getUserRoles(tenantId: $tenantId, userId: $userId) {
          assignmentId
          userId
          tenantId
          roleId
          role {
            roleId
            roleName
            displayName
            scopes
            capabilities
          }
          assignedBy
          assignedAt
          expiresAt
          revoked
          revokedBy
          revokedAt
          revocationReason
        }
      }
    `;

    try {
      const result = await this.apolloClient.query<Record<string, any>>({
        query,
        variables: { tenantId, userId },
        context: {
          headers: { 'x-tenant': tenantId }
        },
        fetchPolicy: 'network-only'
      });

      return result.data?.getUserRoles ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to get user roles:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Mutation Operations (Admin Only)
  // ==========================================================================

  /**
   * Create new role (admin only)
   *
   * @param input - Role creation input
   * @returns Created role definition
   */
  async createRole(input: CreateRoleInput): Promise<RoleDefinition> {
    await this.validateRepositorySafety({
      operation: 'CREATE_ROLE',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'CRITICAL',
      metadata: { roleName: input.roleName, scopeCount: input.scopes.length }
    });

    const mutation = gql`
      mutation CreateRole($input: CreateRoleInput!) {
        createRole(input: $input) {
          roleId
          tenantId
          roleName
          displayName
          description
          scopes
          capabilities
          inheritsFrom
          enabled
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'CREATE_ROLE',
          tenantId: input.tenantId,
          entityType: 'RoleDefinition',
          entityId: result.data?.createRole.roleId,
          metadata: { roleName: input.roleName },
          timestamp: Date.now()
        });
      }

      return result.data?.createRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to create role:', error);
      throw error;
    }
  }

  /**
   * Update existing role (admin only)
   *
   * @param input - Role update input
   * @returns Updated role definition
   */
  async updateRole(input: UpdateRoleInput): Promise<RoleDefinition> {
    await this.validateRepositorySafety({
      operation: 'UPDATE_ROLE',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'CRITICAL',
      metadata: { roleId: input.roleId }
    });

    const mutation = gql`
      mutation UpdateRole($input: UpdateRoleInput!) {
        updateRole(input: $input) {
          roleId
          tenantId
          roleName
          displayName
          description
          scopes
          capabilities
          inheritsFrom
          enabled
          version
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'UPDATE_ROLE',
          tenantId: input.tenantId,
          entityType: 'RoleDefinition',
          entityId: input.roleId,
          metadata: { version: result.data?.updateRole.version },
          timestamp: Date.now()
        });
      }

      return result.data?.updateRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to update role:', error);
      throw error;
    }
  }

  /**
   * Delete role (admin only)
   *
   * @param tenantId - Tenant ID
   * @param roleId - Role ID
   * @returns True if deleted
   */
  async deleteRole(tenantId: string, roleId: string): Promise<boolean> {
    await this.validateRepositorySafety({
      operation: 'DELETE_ROLE',
      domain: 'RBAC',
      tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'CRITICAL',
      metadata: { roleId }
    });

    const mutation = gql`
      mutation DeleteRole($tenantId: String!, $roleId: String!) {
        deleteRole(tenantId: $tenantId, roleId: $roleId)
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { tenantId, roleId },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'DELETE_ROLE',
          tenantId,
          entityType: 'RoleDefinition',
          entityId: roleId,
          metadata: { deleted: result.data?.deleteRole },
          timestamp: Date.now()
        });
      }

      return result.data?.deleteRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to delete role:', error);
      throw error;
    }
  }

  /**
   * Assign role to user (admin only)
   *
   * Creates immutable UserRoleAssignment record
   *
   * @param input - Role assignment input
   * @returns Created assignment
   */
  async assignRole(input: AssignRoleInput): Promise<UserRoleAssignment> {
    await this.validateRepositorySafety({
      operation: 'ASSIGN_ROLE',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'UserRoleAssignment',
      criticalityLevel: 'CRITICAL',
      metadata: { userId: input.userId, roleId: input.roleId }
    });

    const mutation = gql`
      mutation AssignRole($input: AssignRoleInput!) {
        assignRole(input: $input) {
          assignmentId
          userId
          tenantId
          roleId
          role {
            roleName
            displayName
          }
          assignedBy
          assignedAt
          expiresAt
          revoked
        }
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'ASSIGN_ROLE',
          tenantId: input.tenantId,
          entityType: 'UserRoleAssignment',
          entityId: result.data?.assignRole.assignmentId,
          metadata: { userId: input.userId, roleId: input.roleId },
          timestamp: Date.now()
        });
      }

      return result.data?.assignRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to assign role:', error);
      throw error;
    }
  }

  /**
   * Revoke user's role (admin only)
   *
   * Sets revoked flag (immutable, additive only)
   *
   * @param input - Role revocation input
   * @returns True if revoked
   */
  async revokeRole(input: RevokeRoleInput): Promise<boolean> {
    await this.validateRepositorySafety({
      operation: 'REVOKE_ROLE',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'UserRoleAssignment',
      criticalityLevel: 'CRITICAL',
      metadata: { userId: input.userId, roleId: input.roleId, reason: input.reason }
    });

    const mutation = gql`
      mutation RevokeRole($input: RevokeRoleInput!) {
        revokeRole(input: $input)
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { input },
        context: {
          headers: { 'x-tenant': input.tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'REVOKE_ROLE',
          tenantId: input.tenantId,
          entityType: 'UserRoleAssignment',
          entityId: `${input.userId}-${input.roleId}`,
          metadata: { reason: input.reason },
          timestamp: Date.now()
        });
      }

      return result.data?.revokeRole ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to revoke role:', error);
      throw error;
    }
  }

  /**
   * Import permission template (admin only)
   *
   * Creates role from built-in template
   *
   * @param tenantId - Tenant ID
   * @param templateName - Template name (viewer, analyst, operator, curator, approver, admin)
   * @returns Created role from template
   */
  async importPermissionTemplate(tenantId: string, templateName: string): Promise<RoleDefinition> {
    await this.validateRepositorySafety({
      operation: 'IMPORT_TEMPLATE',
      domain: 'RBAC',
      tenantId,
      entityType: 'RoleDefinition',
      criticalityLevel: 'HIGH',
      metadata: { templateName }
    });

    const mutation = gql`
      mutation ImportPermissionTemplate($tenantId: String!, $templateName: String!) {
        importPermissionTemplate(tenantId: $tenantId, templateName: $templateName) {
          roleId
          tenantId
          roleName
          displayName
          description
          scopes
          capabilities
          inheritsFrom
          enabled
          createdAt
          createdBy
        }
      }
    `;

    try {
      const result = await this.apolloClient.mutate<Record<string, any>>({
        mutation,
        variables: { tenantId, templateName },
        context: {
          headers: { 'x-tenant': tenantId }
        }
      });

      if (this.auditTrail) {
        await this.auditTrail.logOperationSuccess({
          operation: 'IMPORT_TEMPLATE',
          tenantId,
          entityType: 'RoleDefinition',
          entityId: result.data?.importPermissionTemplate.roleId,
          metadata: { templateName },
          timestamp: Date.now()
        });
      }

      return result.data?.importPermissionTemplate ?? null;
    } catch (error) {
      console.error('[RoleRepository] Failed to import permission template:', error);
      throw error;
    }
  }
}

export default RoleRepository;
